/**
 * Product dynamic-filter catalog — the admin-v2 subset of the legacy `dynamicFilters` registry
 * (Core/client/admin/src/dynamic-filters/index.js) restricted to `resources: ['products']`.
 *
 * Each leaf maps a persisted `$type` to an editor `kind` the builder knows how to render. Filter
 * types we don't yet have a native editor for (productAttribute, discountBetweenPriceTypes, hasSubTypes,
 * hasModels, policy filters) are listed as `unsupported` so they
 * still appear, round-trip losslessly, and are clearly labelled — rather than silently dropped.
 */
export const FILTER_AND = 'and';
export const FILTER_OR = 'or';
export const FILTER_NOT = 'not';

export type LeafKind = 'ordered-number' | 'ordered-date' | 'topSelling' | 'searchTerm' | 'ids' | 'metricTypes' | 'unsupported';
export type IdsEntity = 'categories' | 'statuses' | 'manufacturers' | 'warehouses' | 'availabilities';

export interface CompoundDef {
	type: typeof FILTER_AND | typeof FILTER_OR | typeof FILTER_NOT;
	label: string;
	compound: true;
	unary: boolean;
}
export interface LeafDef {
	type: string;
	label: string;
	compound: false;
	kind: LeafKind;
	/** ordered-number numeric bounds. */
	min?: number;
	max?: number;
	/** ids entity lookup (when kind === 'ids'). */
	entity?: IdsEntity;
	/** bestSellers supports an optional `limit`. */
	supportsLimit?: boolean;
}
export type FilterDef = CompoundDef | LeafDef;

export const COMPOUND_FILTERS: CompoundDef[] = [
	{ type: FILTER_AND, label: 'AND', compound: true, unary: false },
	{ type: FILTER_OR, label: 'ANY', compound: true, unary: false },
	{ type: FILTER_NOT, label: 'NOT', compound: true, unary: true }
];

export const LEAF_FILTERS: LeafDef[] = [
	{ type: 'daysSinceCreation', label: 'Days Since Creation', compound: false, kind: 'ordered-number', min: 0 },
	{ type: 'daysSinceRelease', label: 'Days Since Release', compound: false, kind: 'ordered-number', min: 0 },
	{ type: 'discount', label: 'Discount', compound: false, kind: 'ordered-number', min: 0, max: 100 },
	{ type: 'initialPrice', label: 'Initial Price', compound: false, kind: 'ordered-number', min: 0 },
	{ type: 'finalPrice', label: 'Final Price', compound: false, kind: 'ordered-number', min: 0 },
	{ type: 'quantity', label: 'Quantity', compound: false, kind: 'ordered-number', min: 0 },
	{ type: 'bestSellers', label: 'Best Sellers', compound: false, kind: 'ordered-date', supportsLimit: true },
	{ type: 'topSelling', label: 'Top Selling', compound: false, kind: 'topSelling' },
	{ type: 'searchTerm', label: 'Search Term', compound: false, kind: 'searchTerm' },
	{ type: 'inCategories', label: 'In Categories', compound: false, kind: 'ids', entity: 'categories' },
	{ type: 'hasStatuses', label: 'Has Statuses', compound: false, kind: 'ids', entity: 'statuses' },
	{ type: 'hasManufacturers', label: 'Has Manufacturers', compound: false, kind: 'ids', entity: 'manufacturers' },
	{ type: 'hasAvailabilities', label: 'Has Availabilities', compound: false, kind: 'ids', entity: 'availabilities' },
	{ type: 'hasAvailableStock', label: 'Available in Warehouse', compound: false, kind: 'ids', entity: 'warehouses' },
	{ type: 'metricTypes', label: 'Metric Types', compound: false, kind: 'metricTypes' },
	// Preserved-but-not-yet-editable (need lookups/editors not yet ported to admin-v2):
	{ type: 'hasSubTypes', label: 'Has SubTypes', compound: false, kind: 'unsupported' },
	{ type: 'hasModels', label: 'Has Models', compound: false, kind: 'unsupported' },
	{ type: 'productAttribute', label: 'Product Attribute', compound: false, kind: 'unsupported' },
	{ type: 'discountBetweenPriceTypes', label: 'Discount Between Price Types', compound: false, kind: 'unsupported' },
	{ type: 'promotedByPolicy', label: 'Promoted By Policy', compound: false, kind: 'unsupported' },
	{ type: 'partakesInPolicy', label: 'Participates In Policy', compound: false, kind: 'unsupported' }
];

export const ALL_FILTERS: FilterDef[] = [...COMPOUND_FILTERS, ...LEAF_FILTERS];

const byType = new Map<string, FilterDef>(ALL_FILTERS.map(f => [f.type, f]));
export const filterDef = (type?: string): FilterDef | undefined => (type ? byType.get(type) : undefined);

/** Product search-term fields (parity with the legacy getAvailableSearchFields('products')). */
export const PRODUCT_SEARCH_FIELDS = ['Name', 'Code', 'Barcode', 'AltCode', 'DescriptionLarge', 'DescriptionSmall'];

export interface FilterNodeValue {
	$type?: string;
	criteria?: FilterNodeValue | FilterNodeValue[];
	op?: string;
	value?: unknown;
	from?: unknown;
	to?: unknown;
	limit?: number | null;
	ids?: number[];
	fields?: string[];
	term?: string;
	Limit?: number;
	DayWindow?: number;
	[k: string]: unknown;
}
